import { PublicRoutes } from "@/constants/routes";
import { Ban, FileSignature, MessageSquareWarning, Scale } from "lucide-react";
import { COMPANY } from "./company";
import type { LegalDocument } from "./types";

export const TERMS_OF_USE: LegalDocument = {
  key: "termos",
  path: PublicRoutes.TERMOS_DE_USO,
  eyebrow: "Contratação",
  title: "Termos de Uso",
  seoTitle: "Termos de Uso: regras do site e da plataforma",
  description:
    "Regras do site e da plataforma Fidexa: conta e acesso, uso permitido, mensagens da loja, pagamento, suspensão, propriedade intelectual e responsabilidade.",
  intro:
    "Estes termos são as regras para usar o site e a plataforma da Fidexa. Para quem contrata, eles se somam ao contrato de licenciamento e à proposta comercial, que prevalecem se algo for diferente.",
  version: "2.0",
  updatedAt: "2026-10-12",

  highlights: [
    {
      icon: FileSignature,
      title: "O contrato vem primeiro",
      text: "Para lojas contratantes, o contrato de licenciamento e a proposta comercial prevalecem sobre estes termos.",
    },
    {
      icon: MessageSquareWarning,
      title: "A mensagem é da loja",
      text: "A loja responde pelo conteúdo, pelo tom e pela frequência das mensagens que envia aos clientes dela.",
    },
    {
      icon: Ban,
      title: "Uso lícito, sempre",
      text: "Nada de cobrança abusiva, dados obtidos sem base legal ou uso por CNPJ não autorizado.",
    },
    {
      icon: Scale,
      title: "Ferramenta, não garantia",
      text: "A Fidexa automatiza a gestão da cobrança. O resultado da recuperação depende de fatores fora da plataforma.",
    },
  ],

  sections: [
    {
      id: "aceite",
      title: "A quem estes termos se aplicam",
      summary:
        "Valem para quem navega no site e para quem usa a plataforma. Quem contrata a Fidexa também segue o contrato assinado.",
      blocks: [
        {
          type: "paragraph",
          text: `Estes termos regem o uso do site fidexa.com.br e da plataforma Fidexa, oferecida pela **${COMPANY.legalName}**, CNPJ ${COMPANY.cnpj} ("Fidexa"). Ao usar o site ou a plataforma, você concorda com eles.`,
        },
        {
          type: "paragraph",
          text: "Para a loja que contrata a Fidexa, valem também o **contrato de licenciamento de uso de software** e a **proposta comercial**. Se houver diferença entre eles e estes termos, prevalece o contrato e, depois dele, a proposta.",
        },
      ],
    },
    {
      id: "o-servico",
      title: "O que a Fidexa oferece",
      summary:
        "Uma plataforma, no modelo de software como serviço, para organizar o crediário e automatizar a comunicação de cobrança. A Fidexa não garante a recuperação dos valores.",
      blocks: [
        {
          type: "paragraph",
          text: "A Fidexa licencia o uso da plataforma em caráter não exclusivo e intransferível, pelo tempo do contrato. A licença inclui o acesso para os usuários e lojas contratados, a implantação e o treinamento, o suporte e as atualizações da plataforma.",
        },
        {
          type: "paragraph",
          text: "A plataforma é um **meio** para a loja gerir a cobrança. A Fidexa não garante a recuperação de dívidas nem o sucesso das cobranças, que dependem de fatores externos, como a situação financeira dos clientes da loja e as estratégias comerciais dela.",
        },
        {
          type: "paragraph",
          text: "Customizações, funcionalidades específicas e integrações fora da proposta comercial não fazem parte do serviço e são negociadas à parte.",
        },
      ],
    },
    {
      id: "conta-e-acesso",
      title: "Conta e acesso",
      summary:
        "Cada usuário tem login próprio e responde pelo que faz na conta. O serviço vale só para os CNPJs contratados.",
      blocks: [
        {
          type: "list",
          items: [
            "As credenciais de acesso são pessoais e sigilosas. A loja responde por todas as atividades feitas na conta dela.",
            "A loja mantém os dados cadastrais atualizados e indica um e-mail válido para comunicações oficiais.",
            "O serviço só pode ser usado pelos CNPJs indicados no contrato ou nos anexos. Incluir um novo CNPJ depende de pedido e aprovação da Fidexa e ajusta a mensalidade conforme a tabela vigente.",
            "O uso por CNPJ não autorizado permite à Fidexa suspender o serviço de imediato, cobrar os valores do uso indevido e aplicar as penalidades do contrato.",
          ],
        },
      ],
    },
    {
      id: "uso-permitido",
      title: "Uso permitido e proibido",
      summary:
        "Use a plataforma dentro da lei e das regras do WhatsApp. É proibido cobrar de forma abusiva, usar dados sem base legal ou tentar copiar a plataforma.",
      blocks: [
        { type: "subheading", text: "A loja se compromete a" },
        {
          type: "list",
          items: [
            "Usar a plataforma conforme a lei, o contrato e as políticas da Meta para o WhatsApp Business, sem prática abusiva ou que viole direitos de terceiros.",
            "Inserir apenas dados verdadeiros e obtidos de forma lícita, com base legal para tratá-los (veja [LGPD na Fidexa](/lgpd)).",
            "Respeitar o cliente que pedir para não receber mais mensagens.",
            "Cooperar com a Fidexa na apuração de problemas técnicos.",
          ],
        },
        { type: "subheading", text: "É proibido" },
        {
          type: "list",
          items: [
            "Usar a plataforma para cobrar com ameaça, coação, constrangimento, afirmações falsas ou exposição do cliente ao ridículo (Código de Defesa do Consumidor, arts. 42 e 71).",
            "Enviar mensagens a quem não é cliente da loja ou cobrar valores que não são devidos.",
            "Copiar, modificar, traduzir, adaptar ou criar obras derivadas da plataforma.",
            "Fazer engenharia reversa, descompilar ou tentar obter o código-fonte.",
            "Sublicenciar, alugar, vender, ceder ou transferir o direito de uso.",
            "Prejudicar a segurança ou o funcionamento do site e da plataforma, ou introduzir código malicioso.",
          ],
        },
      ],
    },
    {
      id: "mensagens",
      title: "Responsabilidade pelas mensagens",
      summary:
        "Quem fala com o cliente é a loja. Ela responde pelo conteúdo, tom, frequência e legalidade das mensagens enviadas pela plataforma.",
      blocks: [
        {
          type: "paragraph",
          text: "A loja é a única responsável pelo conteúdo, pelo tom, pela frequência e pela legalidade das mensagens de cobrança e das demais comunicações feitas pela plataforma, e deve cumprir o Código de Defesa do Consumidor e as demais normas aplicáveis. Se um cliente da loja reclamar, multar ou processar a Fidexa por uma comunicação indevida da loja, a loja assume a defesa e indeniza a Fidexa.",
        },
      ],
    },
    {
      id: "terceiros",
      title: "Serviços de terceiros",
      summary:
        "A plataforma depende de serviços de terceiros, como o WhatsApp da Meta e as integrações com ERPs. A Fidexa não responde por falhas ou mudanças desses serviços.",
      blocks: [
        {
          type: "paragraph",
          text: "A plataforma usa serviços e APIs de terceiros, como a WhatsApp Business Platform da Meta e as integrações com sistemas de gestão. A Fidexa não responde por interrupções, bloqueios, mudanças de política, indisponibilidade ou custos adicionais impostos por esses terceiros.",
        },
      ],
    },
    {
      id: "pagamento",
      title: "Pagamento e reajuste",
      summary:
        "A mensalidade vence no dia 1º de cada mês. Atraso tem multa de 2%, juros de 1% ao mês e correção pelo IPCA; acima de 15 dias, o acesso pode ser suspenso.",
      blocks: [
        {
          type: "list",
          items: [
            "Os valores, a taxa de implantação (quando houver) e os custos variáveis, como o volume de mensagens de WhatsApp, estão na proposta comercial.",
            "A mensalidade vence no **dia 1º** de cada mês e é paga por boleto, transferência ou Pix.",
            "Em caso de atraso, incidem multa de **2%**, juros de mora de **1% ao mês** e correção monetária pelo IPCA, calculados por dia.",
            "Atraso de mais de **15 dias** permite suspender o acesso, mediante aviso, até a quitação.",
            "Os valores são reajustados uma vez por ano pela variação positiva do IPCA/IBGE.",
          ],
        },
      ],
    },
    {
      id: "suspensao",
      title: "Suspensão e bloqueio",
      summary:
        "O acesso só é bloqueado em casos previstos: fraude, risco à segurança, CNPJ não autorizado ou atraso no pagamento.",
      blocks: [
        {
          type: "paragraph",
          text: "Em caso de fraude, violação de segurança ou compartilhamento indevido de credenciais que coloque em risco a plataforma ou terceiros, a Fidexa pode bloquear o acesso temporariamente. A loja é avisada na hora e recebe a comprovação, ou indícios robustos, em até 24 horas. Sem essa comprovação, o bloqueio é levantado e a Fidexa responde pelas perdas e danos comprovados da interrupção injustificada.",
        },
        {
          type: "paragraph",
          text: `As regras de teste grátis, cancelamento, dados e número de WhatsApp estão na [Política de Cancelamento](${PublicRoutes.POLITICA_CANCELAMENTO}).`,
        },
      ],
    },
    {
      id: "propriedade-intelectual",
      title: "Propriedade intelectual",
      summary:
        "A plataforma é da Fidexa. A loja recebe uma licença de uso, não a propriedade. Violar essa regra encerra o contrato na hora.",
      blocks: [
        {
          type: "paragraph",
          text: "A plataforma, incluindo código-fonte, design, interface, funcionalidades, algoritmos e segredos de negócio, é de propriedade exclusiva da Fidexa. Estes termos e o contrato concedem só uma licença de uso temporária, revogável e limitada.",
        },
        {
          type: "paragraph",
          text: "A loja não pode desenvolver, contratar ou ajudar terceiros a criar solução concorrente baseada no conhecimento, na interface, na estrutura lógica ou nas funcionalidades da plataforma. Ela pode desenvolver soluções parecidas apenas para uso interno próprio. A violação destas regras permite a rescisão imediata do contrato, sem prejuízo de perdas e danos e das sanções cabíveis.",
        },
      ],
    },
    {
      id: "responsabilidade",
      title: "Limites de responsabilidade",
      summary:
        "A Fidexa responde pelos danos que causar por dolo ou culpa exclusiva, comprovados. Não responde por lucros cessantes, perdas indiretas ou falhas fora do controle dela.",
      blocks: [
        {
          type: "paragraph",
          text: "A Fidexa não responde por danos indiretos, especiais, incidentais, punitivos ou morais, nem por lucros cessantes, perda de receita, de oportunidade ou de dados, salvo quando causados comprovadamente por dolo ou culpa exclusiva dela. Nesses casos, a responsabilidade se limita aos danos comprovados.",
        },
        {
          type: "paragraph",
          text: "Também não responde por falhas causadas por caso fortuito ou força maior, por problemas de hardware, software ou internet da loja ou de terceiros, ou por uso indevido da plataforma pela loja ou pelos usuários dela.",
        },
        {
          type: "paragraph",
          text: "Como fornecedora da plataforma e operadora dos dados, a Fidexa responde pela guarda e segurança dos dados inseridos nela, inclusive por perda total ou parcial causada por culpa exclusiva sua. Recomendamos que a loja mantenha também cópias próprias dos dados essenciais.",
        },
      ],
    },
    {
      id: "mudancas",
      title: "Mudanças no serviço e nestes termos",
      summary:
        "Mudanças relevantes são avisadas com 30 dias de antecedência. Se não concordar, a loja pode cancelar sem custo antes de elas valerem.",
      blocks: [
        {
          type: "paragraph",
          text: "A Fidexa pode modificar, incluir ou remover funcionalidades e alterar estes termos e os valores dos planos, mantendo as funcionalidades básicas. A loja é avisada com pelo menos **30 dias** de antecedência. Se não concordar, pode cancelar sem ônus antes de as mudanças entrarem em vigor.",
        },
      ],
    },
    {
      id: "dados-e-marca",
      title: "Dados pessoais e uso da marca",
      summary:
        "O tratamento de dados segue a Política de Privacidade. A Fidexa pode citar a loja como cliente, até que ela peça para parar.",
      blocks: [
        {
          type: "paragraph",
          text: `O tratamento de dados pessoais segue a [Política de Privacidade](${PublicRoutes.POLITICA_PRIVACIDADE}) e a página [LGPD na Fidexa](${PublicRoutes.LGPD}).`,
        },
        {
          type: "paragraph",
          text: "A loja autoriza a Fidexa, de forma gratuita e não exclusiva, a usar o nome e o logotipo dela em materiais de marketing, apresentações e no site, só para identificá-la como cliente. A Fidexa segue o manual de marca que a loja fornecer e para de usar a marca se a loja pedir por escrito.",
        },
      ],
    },
    {
      id: "foro",
      title: "Disposições gerais e foro",
      summary:
        "Tolerância não é renúncia. Disputas são resolvidas no foro de Piracicaba/SP.",
      blocks: [
        {
          type: "list",
          items: [
            "Se uma parte tolerar o descumprimento de alguma obrigação, isso não significa renúncia nem alteração do que foi combinado.",
            "Se uma cláusula for considerada inválida, as demais continuam valendo.",
            "Estes termos são regidos pelas leis do Brasil. Fica eleito o foro da Comarca de Piracicaba/SP para resolver qualquer controvérsia.",
          ],
        },
      ],
    },
  ],

  changelog: [
    {
      version: "2.0",
      date: "2026-10-12",
      changes: [
        "Termos reescritos a partir do contrato de licenciamento, em linguagem simples e com resumo por seção.",
        "Inclusão das regras de uso, de pagamento, de suspensão e de responsabilidade pelas mensagens.",
        "Substitui o texto genérico anterior.",
      ],
    },
  ],
};
